import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { LEGAL_AREAS } from '../legal-areas';
import { LeadRequestService } from '../lead-request.service';
import { PreferredContact } from '../models';
import { finalize, switchMap, take } from 'rxjs/operators';

export interface LeadRequestDialogData {
  legalAreaSlug?: string;
}

@Component({
  selector: 'app-lead-request-dialog',
  templateUrl: './lead-request-dialog.component.html',
  styleUrls: ['./lead-request-dialog.component.scss']
})
export class LeadRequestDialogComponent {
  legalAreas = LEGAL_AREAS;
  form: FormGroup;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private leadRequestService: LeadRequestService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<LeadRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LeadRequestDialogData
  ) {
    this.form = this.fb.group(
      {
        legal_area_slug: [data && data.legalAreaSlug ? data.legalAreaSlug : null, [Validators.required]],
        name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(120)]],
        email: [null, [Validators.email, Validators.maxLength(190)]],
        phone: ['', [Validators.required, Validators.maxLength(30), Validators.pattern(/^\+?[1-9]\d{7,14}$/)]],
        description: ['', [Validators.required, Validators.maxLength(300)]],
        preferred_contact: ['whatsapp', [Validators.required]]
      },
      { validators: [this.emailRequiredIfPreferredEmailValidator] }
    );
  }

  get descriptionLength(): number {
    const v = this.form.get('description') ? this.form.get('description').value : '';
    return (v || '').length;
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.isSubmitting) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this.isSubmitting = true;
    const value = this.form.value;
    this.recaptchaV3Service
      .execute('lead_request')
      .pipe(
        take(1),
        switchMap((token: string) =>
          this.leadRequestService.create({
            legal_area_slug: value.legal_area_slug,
            name: value.name,
            email: value.email,
            phone: value.phone,
            description: value.description,
            preferred_contact: value.preferred_contact,
            recaptcha_token: token
          })
        ),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe(
        () => {
          this.snackBar.open('Solicitud enviada. Te contactaremos pronto.', 'Cerrar', { duration: 5000 });
          this.dialogRef.close(true);
        },
        (error: HttpErrorResponse) => {
          this.snackBar.open(this.getSubmitErrorMessage(error), 'Cerrar', { duration: 7000 });
        }
      );
  }

  private getSubmitErrorMessage(error: HttpErrorResponse): string {
    if (!error || error.status === 0) {
      return 'No se pudo conectar con el servidor. Revisa que el API esté activo.';
    }

    if (error.error && error.error.errors) {
      const errors = error.error.errors;
      const firstKey = Object.keys(errors)[0];
      const firstMessage = firstKey && errors[firstKey] && errors[firstKey][0] ? errors[firstKey][0] : null;

      if (firstMessage) {
        return firstMessage;
      }
    }

    return error.error && error.error.message ? error.error.message : 'No se pudo enviar. Intenta de nuevo.';
  }

  private emailRequiredIfPreferredEmailValidator(group: FormGroup) {
    const preferred: PreferredContact = group.get('preferred_contact')
      ? (group.get('preferred_contact').value as PreferredContact)
      : 'whatsapp';
    const email = group.get('email') ? group.get('email').value : null;

    if (preferred === 'email' && (!email || String(email).trim().length === 0)) {
      return { emailRequired: true };
    }
    return null;
  }
}
